import { Service } from 'components/system';
import { A9Service } from 'components/system/angular/A9Service';
import { RsCustomerCounterInfo } from './types/RsCustomerCounterInfo';

/**
 * #18419
 * Below is an algorithm how to generate correct number for a customer
 * 1) When started customer creation wizard execute POST /rs/customers/dict/sub-types/{id}/custno with parameters
 * increase: false, force: false. It is a CURRENT value of customer number, used for last created customer of this type.
 * So when you show this number to a user, increase it by 1, for example for "curValue": 1016 display 1017.
 * 2) When user pressed save and customer is about to be really created - use
 * POST /rs/customers/dict/sub-types/{id}/custno with parameters increase: true, force: false. Store this value in cool
 * and dry place - even if customer is not created later due to the errors do not call POST
 * /rs/customers/dict/sub-types/{id}/custno function again - number is already generated and should be used to have no
 * gaps in customer numbers.
 * If POST /rs/customers/dict/sub-types/{id}/custno with parameters increase: true, force: false returned
 * CustomerCounterValueOccupiedException try to execute it with parameters increase: true, force: true - it will return
 * the next not occupied number. If it returns error CustomerCounterHasNoMoreValuesException you are not able to
 * generate numbers anymore and counter should be changed in customization area.
 */

@A9Service('nextCustomerNumberService')
export class NextCustomerNumberService extends Service {
    constructor(private $http: angular.IHttpService) {
        super();
    }

    public getPreviewNumber(subTypeId: number): Promise<number> {
        const url = `middle://rs/customers/dict/sub-types/${subTypeId}/custno`;
        return Promise.resolve(
            this.$http
                .post<RsCustomerCounterInfo>(url, {
                    force: false,
                    increase: false
                })
                .then(result => {
                    if (!result.data || result.data.curValue + 1 > result.data.maxValue || result.data.curValue + 1 < result.data.minValue) {
                        throw new Error('Bad customer number');
                    }
                    return result.data.curValue + 1;
                })
        );
    }

    public getNextNumber(subTypeId: number): Promise<number> {
        const url = `middle://rs/customers/dict/sub-types/${subTypeId}/custno`;
        return Promise.resolve(
            this.$http
                .post<RsCustomerCounterInfo>(url, {
                    force: false,
                    increase: true
                })
                .catch(error => {
                    const errorCodes = ['CustomerNumberOccupiedByTenant', 'CustomerCounterValueOccupied'];
                    if (errorCodes.includes(error.code) || error.result?.details?.some(detail => errorCodes.includes(detail.code))) {
                        return this.$http.post<RsCustomerCounterInfo>(url, {
                            force: true,
                            increase: true
                        });
                    }
                    throw error;
                })
                .then(result => {
                    return result.data.curValue;
                })
        );
    }
}
